import React, { useEffect, useState, useCallback } from "react";
import { CalendarClock, Loader2, Download, RefreshCw, Edit2, Check, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_PILL = {
  present: "bg-emerald-100 text-emerald-700",
  wfh: "bg-blue-100 text-blue-700",
  leave: "bg-amber-100 text-amber-700",
  half_day: "bg-orange-100 text-orange-700",
  absent: "bg-red-100 text-red-600",
};
const STATUSES = ["present", "wfh", "leave", "half_day", "absent"];

const todayStr = () => new Date().toISOString().slice(0, 10);
const nDaysAgoStr = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const AttendancePanel = ({ authAxios }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(nDaysAgoStr(7));
  const [dateTo, setDateTo] = useState(todayStr());
  const [userFilter, setUserFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editStatus, setEditStatus] = useState("present");
  const [editNotes, setEditNotes] = useState("");

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/attendance`, {
        params: { date_from: dateFrom, date_to: dateTo, user_key: userFilter || undefined },
      });
      setRows(r.data?.items || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [authAxios, dateFrom, dateTo, userFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const exportCsv = async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/attendance/export`, {
        params: { date_from: dateFrom, date_to: dateTo },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `attendance_${dateFrom}_to_${dateTo}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      alert("Export failed — try a smaller date range.");
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditStatus(row.status || "present");
    setEditNotes(row.notes || "");
  };

  const saveEdit = async () => {
    try {
      await authAxios.put(`${API_URL}/api/admin/attendance/${editingId}`, {
        status: editStatus, notes: editNotes,
      });
      setEditingId(null); setEditNotes(""); fetchRows();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="attendance-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#10B981] to-[#0EA5E9] flex items-center justify-center">
            <CalendarClock size={20} className="text-white" />
          </div>
          <div>
            <h3 className="font-bold text-[#1E1B4B] text-sm">Attendance</h3>
            <p className="text-xs text-[#94a3b8]">
              IP-tracked login/logout · auto-closes idle sessions after 30 min.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchRows}
            className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
            title="Refresh"
            data-testid="refresh-attendance"
          ><RefreshCw size={14} /></button>
          <button
            onClick={exportCsv}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-[11px] font-bold hover:bg-emerald-100"
            data-testid="export-attendance-csv"
          ><Download size={12} /> CSV</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg" data-testid="attendance-from" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg" data-testid="attendance-to" />
        <input value={userFilter} onChange={(e) => setUserFilter(e.target.value)}
          placeholder="Filter by username / phone"
          className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg flex-1 min-w-[160px]"
          data-testid="attendance-user-filter" />
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-[#E83E8C]" /></div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-[#94a3b8] text-center py-4">No attendance rows in this range.</p>
      ) : (
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-[11px]">
            <thead className="bg-gray-50 text-gray-500 uppercase">
              <tr>
                <th className="px-2 py-1.5 text-left">Date</th>
                <th className="px-2 py-1.5 text-left">Name</th>
                <th className="px-2 py-1.5 text-left">Role</th>
                <th className="px-2 py-1.5 text-left">In</th>
                <th className="px-2 py-1.5 text-left">Out</th>
                <th className="px-2 py-1.5 text-left">Hrs</th>
                <th className="px-2 py-1.5 text-left">IP</th>
                <th className="px-2 py-1.5 text-left">Status</th>
                <th className="px-2 py-1.5 text-left"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`attendance-row-${r.id}`}>
                  <td className="px-2 py-1.5 font-mono">{r.date}</td>
                  <td className="px-2 py-1.5">{r.name || r.username}</td>
                  <td className="px-2 py-1.5 font-mono text-gray-500">{r.role || "-"}</td>
                  <td className="px-2 py-1.5">{(r.login_at || "").slice(11, 16)}</td>
                  <td className="px-2 py-1.5">{(r.logout_at || "").slice(11, 16) || <span className="text-emerald-600">—</span>}</td>
                  <td className="px-2 py-1.5 font-semibold">{r.hours ?? "-"}</td>
                  <td className="px-2 py-1.5 font-mono text-gray-500">{r.login_ip || "-"}</td>
                  <td className="px-2 py-1.5">
                    {editingId === r.id ? (
                      <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}
                        className="text-[10px] px-1 py-0.5 border rounded"
                        data-testid={`attendance-status-edit-${r.id}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_PILL[r.status] || "bg-gray-100 text-gray-600"}`}>
                        {r.status}{r.auto_half_day ? " (< 4h)" : ""}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {editingId === r.id ? (
                      <div className="flex gap-1">
                        <input value={editNotes} onChange={(e) => setEditNotes(e.target.value)}
                          placeholder="notes" className="text-[10px] px-1 py-0.5 border rounded w-20"
                          data-testid={`attendance-notes-edit-${r.id}`} />
                        <button onClick={saveEdit} className="p-1 rounded bg-emerald-500 text-white"
                          data-testid={`save-attendance-${r.id}`}><Check size={10} /></button>
                        <button onClick={() => setEditingId(null)} className="p-1 rounded bg-gray-100"><X size={10} /></button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(r)} className="text-gray-400 hover:text-[#E83E8C]"
                        data-testid={`edit-attendance-${r.id}`}><Edit2 size={11} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AttendancePanel;
